import { useEffect, useState } from 'react'
import { useEventCallback } from 'studiokit-scaffolding-js/lib/hooks/useEventCallback'
import { useFetchData } from 'studiokit-scaffolding-js/lib/hooks/useFetchData'
import { FETCH_STATUS } from '../types/FetchStatus'
import { UserInfo } from '../types/UserInfo'

/**
 * Loads the authenticated user's info. Lives above the routes because both the header and the
 * conversion screens need `hasAccess`.
 *
 * @param isAuthenticated Whether there is an authenticated user.
 */
export function useUserInfo(isAuthenticated: boolean) {
	const [userInfo, setUserInfo] = useState<UserInfo | undefined>()
	const { send: getUserInfo, status: userInfoStatus } = useFetchData(
		'user.userInfo',
		(data: unknown) => {
			setUserInfo(data as UserInfo)
		},
		() => {
			setUserInfo(undefined)
		}
	)

	// automatically load `userInfo` once authenticated, if not set and not already fetching
	useEffect(() => {
		if (isAuthenticated && userInfo === undefined && userInfoStatus === FETCH_STATUS.READY) {
			getUserInfo()
		}
	}, [getUserInfo, isAuthenticated, userInfo, userInfoStatus])

	// automatically clear `userInfo` once un-authenticated
	const onUnauthenticated = useEventCallback(() => {
		setUserInfo(undefined)
	})
	useEffect(() => {
		if (!isAuthenticated && userInfo !== undefined) {
			onUnauthenticated()
		}
	}, [userInfo, isAuthenticated, onUnauthenticated])

	return { userInfo, userInfoStatus }
}
